import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DiamondIcon from '@mui/icons-material/Diamond';
import CategoryIcon from '@mui/icons-material/Category';
import OpacityIcon from '@mui/icons-material/Opacity';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CastIcon from '@mui/icons-material/Cast';

const sidebarStyle = (open) => ({
  width: open ? 240 : 0,
  background: '#fff',
  borderRight: open ? '1.5px solid #e2e8f0' : 'none',
  height: `calc(100vh - 72px)`,
  position: 'fixed',
  top: 72,
  left: 0,
  zIndex: 999,
  padding: open ? '2rem 0' : 0,
  boxSizing: 'border-box',
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  gap: '0.5rem',
  transition: 'width 0.2s, padding 0.2s',
  overflowY: 'auto',
  overflowX: 'hidden',
  borderTopRightRadius: 'var(--radius)',
  borderBottomRightRadius: 'var(--radius)',
  boxShadow: 'var(--shadow)',
});

const menuItemBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.75rem 2rem',
  fontSize: 18,
  cursor: 'pointer',
  borderRadius: '8px',
  letterSpacing: 0.2,
  transition: 'all 0.3s ease',
  color: '#111',
  margin: '0 1rem',
};

const iconStyle = {
  marginRight: 16,
  fontSize: 22,
  display: 'flex',
  alignItems: 'center',
  color: '#00C853',
  transition: 'color 0.3s ease',
};

const submenuStyleBase = {
  paddingLeft: 48,
  fontSize: 16,
  cursor: 'pointer',
  marginTop: 4,
  marginBottom: 4,
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  color: '#111',
  margin: '0 1rem',
  padding: '0.5rem 2rem',
};

const activeBg = '#E8F5E9';
const hoverBg = '#B9F6CA';

const Sidebar = () => {
  const sidebarOpen = useSelector(state => state.ui.sidebarOpen);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hovered, setHovered] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMastersOpen(location.pathname.startsWith('/masters'));
    setSettingsOpen(location.pathname.startsWith('/settings'));
  }, [location.pathname]);

  const handleMastersClick = () => {
    setMastersOpen(prev => !prev);
    navigate('/masters');
  };

  const handleSettingsClick = () => {
    setSettingsOpen(prev => !prev);
    navigate('/settings');
  };

  const isActive = (path) => location.pathname === path;
  const isSubActive = (prefix) => location.pathname.startsWith(prefix);
  const isUserTab = () =>
    ['/masters/user/users', '/masters/user/role', '/masters/user/permission'].some(p =>
      location.pathname.startsWith(p)
    );
  const isCompanyTab = () => location.pathname.startsWith('/masters/company');
  const isProductAttributeTab = () => location.pathname.startsWith('/masters/styles/product-attribute');
  const isStoneAttributeTab = () => location.pathname.startsWith('/masters/stone-attribute');
  const isOrderTypeTab = () => location.pathname.startsWith('/masters/order-type');
  const isMouldTab = () => location.pathname.startsWith('/masters/mould');

  const getBg = (active, hoverKey) =>
    active ? activeBg : hovered === hoverKey ? hoverBg : 'transparent';

  const getTextColor = (active, hoverKey) =>
    active || hovered === hoverKey ? '#00C853' : '#111';

  const getIconColor = (active) => active ? '#00C853' : '#00C853';

  return (
    <aside style={sidebarStyle(sidebarOpen)}>
      {/* Dashboard */}
      <div
        style={{
          ...menuItemBaseStyle,
          background: getBg(isActive('/dashboard'), 'dashboard'),
          fontWeight: isActive('/dashboard') ? 700 : 500,
          color: getTextColor(isActive('/dashboard'), 'dashboard'),
        }}
        onClick={() => navigate('/dashboard')}
        onMouseEnter={() => setHovered('dashboard')}
        onMouseLeave={() => setHovered('')}
      >
        <span style={{ ...iconStyle, color: getIconColor(isActive('/dashboard')) }}>
          <HomeIcon fontSize="inherit" />
        </span>
        Dashboard
      </div>

      {/* Masters */}
      <div
        style={{
          ...menuItemBaseStyle,
          background: getBg(isSubActive('/masters'), 'masters'),
          fontWeight: isSubActive('/masters') ? 700 : 500,
          color: getTextColor(isSubActive('/masters'), 'masters'),
        }}
        onClick={handleMastersClick}
        onMouseEnter={() => setHovered('masters')}
        onMouseLeave={() => setHovered('')}
      >
        <span style={{ ...iconStyle, color: getIconColor(isSubActive('/masters')) }}>
          <PeopleIcon fontSize="inherit" />
        </span>
        Masters
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: hovered === 'masters' ? '#E8F5E9' : 'transparent',
            transition: 'background 0.2s, transform 0.3s',
            color: getIconColor(isSubActive('/masters')),
            transform: mastersOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <KeyboardArrowRightIcon style={{ fontSize: 28, fontWeight: 700 }} />
        </span>
      </div>

      {mastersOpen && (
        <>
          {/* Submenus */}
          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isUserTab(), 'user'),
              fontWeight: isUserTab() ? 600 : 400,
              color: getTextColor(isUserTab(), 'user'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/user/users')}
            onMouseEnter={() => setHovered('user')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><PeopleIcon fontSize="inherit" /></span>
            User
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isCompanyTab(), 'company'),
              fontWeight: isCompanyTab() ? 600 : 400,
              color: getTextColor(isCompanyTab(), 'company'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/company')}
            onMouseEnter={() => setHovered('company')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><BusinessIcon fontSize="inherit" /></span>
            Company
          </div>

          {/* ✅ Updated Styles to Product Attribute */}
          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isProductAttributeTab(), 'product-attribute'),
              fontWeight: isProductAttributeTab() ? 600 : 400,
              color: getTextColor(isProductAttributeTab(), 'product-attribute'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/styles/category-group')}
            onMouseEnter={() => setHovered('product-attribute')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><CategoryIcon fontSize="inherit" /></span>
            Product Attribute
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isStoneAttributeTab(), 'stone-attribute'),
              fontWeight: isStoneAttributeTab() ? 600 : 400,
              color: getTextColor(isStoneAttributeTab(), 'stone-attribute'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/stone-attribute')}
            onMouseEnter={() => setHovered('stone-attribute')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><DiamondIcon fontSize="inherit" /></span>
            Stone Attribute
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(location.pathname.startsWith('/masters/department'), 'department'),
              fontWeight: location.pathname.startsWith('/masters/department') ? 600 : 400,
              color: getTextColor(location.pathname.startsWith('/masters/department'), 'department'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/department')}
            onMouseEnter={() => setHovered('department')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><GroupWorkIcon fontSize="inherit" /></span>
            Department
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(location.pathname.startsWith('/masters/uom'), 'uom'),
              fontWeight: location.pathname.startsWith('/masters/uom') ? 600 : 400,
              color: getTextColor(location.pathname.startsWith('/masters/uom'), 'uom'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/uom')}
            onMouseEnter={() => setHovered('uom')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><CategoryIcon fontSize="inherit" /></span>
            UOM
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(location.pathname.startsWith('/masters/metal-purity'), 'metal-purity'),
              fontWeight: location.pathname.startsWith('/masters/metal-purity') ? 600 : 400,
              color: getTextColor(location.pathname.startsWith('/masters/metal-purity'), 'metal-purity'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/metal-purity')}
            onMouseEnter={() => setHovered('metal-purity')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><OpacityIcon fontSize="inherit" /></span>
            Metal Purity
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isOrderTypeTab(), 'order-type'),
              fontWeight: isOrderTypeTab() ? 600 : 400,
              color: getTextColor(isOrderTypeTab(), 'order-type'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/order-type')}
            onMouseEnter={() => setHovered('order-type')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><ReceiptIcon fontSize="inherit" /></span>
            Order Type
          </div>

          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isMouldTab(), 'mould'),
              fontWeight: isMouldTab() ? 600 : 400,
              color: getTextColor(isMouldTab(), 'mould'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/mould')}
            onMouseEnter={() => setHovered('mould')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><CastIcon fontSize="inherit" /></span>
            Mould
          </div>

          {/* Route Master */}
          <div
            style={{
              ...submenuStyleBase,
              background: getBg(isSubActive('/masters/route'), 'route'),
              fontWeight: isSubActive('/masters/route') ? 600 : 400,
              color: getTextColor(isSubActive('/masters/route'), 'route'),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onClick={() => navigate('/masters/route')}
            onMouseEnter={() => setHovered('route')}
            onMouseLeave={() => setHovered('')}
          >
            <span style={{ ...iconStyle, fontSize: 15 }}><SwapHorizIcon fontSize="inherit" /></span>
            Route
          </div>
        </>
      )}

      {/* Transaction */}
      <div
        style={{
          ...menuItemBaseStyle,
          background: getBg(isActive('/transaction'), 'transaction'),
          fontWeight: isActive('/transaction') ? 700 : 500,
          color: getTextColor(isActive('/transaction'), 'transaction'),
        }}
        onClick={() => navigate('/transaction')}
        onMouseEnter={() => setHovered('transaction')}
        onMouseLeave={() => setHovered('')}
      >
        <span style={{ ...iconStyle }}><SwapHorizIcon fontSize="inherit" /></span>
        Transaction
      </div>

      {/* Reports */}
      <div
        style={{
          ...menuItemBaseStyle,
          background: getBg(isActive('/reports'), 'reports'),
          fontWeight: isActive('/reports') ? 700 : 500,
          color: getTextColor(isActive('/reports'), 'reports'),
        }}
        onClick={() => navigate('/reports')}
        onMouseEnter={() => setHovered('reports')}
        onMouseLeave={() => setHovered('')}
      >
        <span style={{ ...iconStyle }}><BarChartIcon fontSize="inherit" /></span>
        Reports
      </div>

      {/* Settings */}
      <div
        style={{
          ...menuItemBaseStyle,
          background: getBg(isSubActive('/settings'), 'settings'),
          fontWeight: isSubActive('/settings') ? 700 : 500,
          color: getTextColor(isSubActive('/settings'), 'settings'),
        }}
        onClick={handleSettingsClick}
        onMouseEnter={() => setHovered('settings')}
        onMouseLeave={() => setHovered('')}
      >
        <span style={{ ...iconStyle }}><SettingsIcon fontSize="inherit" /></span>
        Settings
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: hovered === 'settings' ? '#E8F5E9' : 'transparent',
            transition: 'background 0.2s, transform 0.3s',
            transform: settingsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <KeyboardArrowRightIcon style={{ fontSize: 28 }} />
        </span>
      </div>

      {settingsOpen && (
        <div
          style={{
            ...submenuStyleBase,
            background: getBg(isActive('/settings/role-permissions'), 'role-permissions'),
            fontWeight: isActive('/settings/role-permissions') ? 600 : 400,
            color: getTextColor(isActive('/settings/role-permissions'), 'role-permissions'),
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
          onClick={() => navigate('/settings/role-permissions')}
          onMouseEnter={() => setHovered('role-permissions')}
          onMouseLeave={() => setHovered('')}
        >
          <span style={{ ...iconStyle, fontSize: 20 }}><SettingsIcon fontSize="inherit" /></span>
          Role Permissions
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
